import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Lead } from '../../types/lead';
import { representativeService } from '../../services/representative.service';
import { leadGroupService } from '../../services/leadGroup.service';
import {
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormPhone,
  FormMail,
  FormSelect,
  Button,
} from '../ui';
import { formatPhoneNumber } from '../ui/FormPhone';

interface LeadFormProps {
  lead: Lead | null;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

function LeadForm({ lead, onSubmit, isLoading, onClose }: LeadFormProps) {
  const [formData, setFormData] = useState({
    full_name: lead?.full_name || '',
    email: lead?.email || '',
    phone: lead?.phone ? formatPhoneNumber(lead.phone) : '',
    phone2: lead?.phone2 ? formatPhoneNumber(lead.phone2) : '',
    phone3: lead?.phone3 ? formatPhoneNumber(lead.phone3) : '',
    representative_id: lead?.representative_id?.toString() || '',
    origin: lead?.origin || '',
    last_contact_at: lead?.last_contact_at
      ? new Date(lead.last_contact_at).toISOString().slice(0, 16)
      : '',
    integration_key: lead?.integration_key?.toString() || '',
    lead_group_ids: lead?.lead_group_ids?.map(String) || [],
    type: lead?.type || 'other',
  });

  const { data: representativesData } = useQuery({
    queryKey: ['representatives-all'],
    queryFn: () => representativeService.getAll(1, 100, { active: true }),
  });

  const { data: leadGroups } = useQuery({
    queryKey: ['lead-groups'],
    queryFn: () => leadGroupService.getAll(),
  });

  const representatives = representativesData?.data || [];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, lead_group_ids: selectedOptions }));
  };

  return (
    <Form onSubmit={onSubmit}>
      <FormField>
        <FormLabel htmlFor="full_name" required>
          Nome Completo
        </FormLabel>
        <FormInput
          id="full_name"
          name="full_name"
          type="text"
          maxLength={200}
          value={formData.full_name}
          onChange={handleChange}
          placeholder="Digite o nome completo do lead"
          required
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="email">E-mail</FormLabel>
        <FormMail
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="exemplo@email.com"
        />
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField>
          <FormLabel htmlFor="phone">Fone Primário</FormLabel>
          <FormPhone
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(00) 00000-0000"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="phone2">Fone Secundário</FormLabel>
          <FormPhone
            id="phone2"
            name="phone2"
            value={formData.phone2}
            onChange={handleChange}
            placeholder="(00) 00000-0000"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="phone3">Fone Terciário</FormLabel>
          <FormPhone
            id="phone3"
            name="phone3"
            value={formData.phone3}
            onChange={handleChange}
            placeholder="(00) 00000-0000"
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField>
          <FormLabel htmlFor="representative_id">Vendedor</FormLabel>
          <FormSelect
            id="representative_id"
            name="representative_id"
            value={formData.representative_id}
            onChange={handleChange}
          >
            <option value="">Nenhum</option>
            {representatives.map(rep => (
              <option key={rep.id} value={rep.id}>
                {rep.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="origin">Origem</FormLabel>
          <FormInput
            id="origin"
            name="origin"
            type="text"
            maxLength={100}
            value={formData.origin}
            onChange={handleChange}
            placeholder="Ex: Website, Indicação, etc."
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="type">Tipo de Lead</FormLabel>
          <FormSelect id="type" name="type" value={formData.type} onChange={handleChange}>
            <option value="other">Outro</option>
            <option value="administrative">Administrativo</option>
            <option value="registration">Cadastro</option>
            <option value="commercial">Comercial</option>
            <option value="financial">Financeiro</option>
            <option value="legal_representative">Representante Legal</option>
            <option value="technical">Técnico</option>
          </FormSelect>
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="last_contact_at">Último Contato</FormLabel>
          <FormInput
            id="last_contact_at"
            name="last_contact_at"
            type="datetime-local"
            value={formData.last_contact_at}
            onChange={handleChange}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="integration_key">Chave de Integração</FormLabel>
          <FormInput
            id="integration_key"
            name="integration_key"
            type="number"
            value={formData.integration_key}
            onChange={handleChange}
            placeholder="Chave de referência"
          />
        </FormField>
      </div>

      <FormField>
        <FormLabel htmlFor="lead_group_ids">Grupos de Leads</FormLabel>
        <select
          id="lead_group_ids"
          name="lead_group_ids"
          multiple
          value={formData.lead_group_ids}
          onChange={handleMultiSelectChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          size={5}
        >
          {leadGroups?.map(group => (
            <option key={group.id} value={group.id}>
              {group.description}
            </option>
          ))}
        </select>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Segure Ctrl (ou Cmd no Mac) para selecionar múltiplos grupos
        </p>
      </FormField>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {lead ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default LeadForm;
